import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { IconQuestionmark } from '@adc/parallax-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiStatus } from 'Enums';
import { useApiError, useAuth } from 'Hooks';
import { Banner, Footer, Form, Header, Input, InputPassword, Link, LogoActionBar, Main, ScrollContainer, Spinner, } from 'Components/utility';
import { AUTH_SESSION } from 'Reducers/auth/types';
import { notifyApp } from 'Utilities/appCommunicator';
import { setUrls } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { getLegacyAuthSession, getLegacyToken, signIn } from 'Services/auth';
const initialValues = {
    email: '',
    password: '',
};
const mapStateToProps = ({ env: { app }, config }) => {
    return {
        app,
        config,
    };
};
const connector = connect(mapStateToProps);
const Login = ({ app, config }) => {
    const { showApiErrorModal } = useApiError();
    const { handleLoginError, handleLockoutError, showCountryRegionError, showHcpError, isLocked, lockoutMessage, lockoutSubmessage, } = useAuth();
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'))
            .required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
        password: Yup.string().required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
    });
    const handleWrongRegionError = useCallback((error, handleSubmit) => {
        const unsupportedCountry = error.response?.data.errors[0]?.unsupportedCountry;
        if (!unsupportedCountry) {
            showApiErrorModal();
            return;
        }
        setUrls(config, unsupportedCountry);
        handleSubmit();
    }, [config, showApiErrorModal]);
    const handleApiRequestError = useCallback((error, resetForm, handleSubmit) => {
        const data = error.response?.data
            ? error.response.data
            : { details: i18n.t('Global.modals.errorCommunicatingServer.body') };
        notifyApp('adc-webview:signin', { status: error.status, data });
        switch (error.status) {
            case ApiStatus.UNAUTHORIZED: {
                const code = error.response?.data.code;
                if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
                    showHcpError();
                    return;
                }
                handleLoginError(error, resetForm);
                break;
            }
            case ApiStatus.BAD_REQUEST: {
                const code = error.response?.data.code;
                if (code === ApiStatus.UNSUPPORTED_COUNTRY) {
                    showCountryRegionError();
                    return;
                }
                if (code === ApiStatus.WRONG_REGION) {
                    handleWrongRegionError(error, handleSubmit);
                    return;
                }
                showApiErrorModal();
                break;
            }
            case ApiStatus.TOO_MANY_REQUESTS: {
                handleLockoutError(error);
                resetForm();
                break;
            }
            default:
                showApiErrorModal();
        }
    }, [
        showApiErrorModal,
        handleLoginError,
        showHcpError,
        showCountryRegionError,
        handleWrongRegionError,
        handleLockoutError,
    ]);
    const onSubmit = useCallback(async (values, { setSubmitting, resetForm, submitForm }) => {
        try {
            setSubmitting(true);
            const { email, password } = values;
            const { accessToken, accountId } = await signIn(email, password.trim(), app);
            const legacyToken = await getLegacyToken(app, accountId, accessToken);
            const authSession = await getLegacyAuthSession(legacyToken);
            persistAuthSession(authSession);
            store.dispatch({ type: AUTH_SESSION, authSession });
            notifyApp('adc-webview:signin', authSession);
            mediator.publish('router:navigate', '/connections');
        }
        catch (err) {
            const error = err;
            handleApiRequestError(error, resetForm, submitForm);
        }
        finally {
            setSubmitting(false);
        }
    }, [app, handleApiRequestError]);
    const handleClickForgotPass = useCallback(() => {
        mediator.publish('router:navigate', '/forgot-password');
    }, []);
    const { handleSubmit, handleChange, handleBlur, setFieldValue, isSubmitting, isValid, errors, touched, values, } = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
        validateOnBlur: true,
        initialErrors: {
            email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
            password: i18n.t('Login.content.signInForm.formField.email.errors.required'),
        },
    });
    return (React.createElement(Main, null,
        React.createElement(LogoActionBar, { testID: "Login", title: i18n.t('Login.title') }),
        isSubmitting && React.createElement(Spinner, { testID: "Login" }),
        React.createElement(ScrollContainer, null,
            React.createElement(Header, { testID: "Login", title: i18n.t('Login.title'), subtitle: i18n.t('Login.subtitle', {
                    appName: i18n.t('Global.appNames.libreView'),
                }) }),
            React.createElement(Banner, { testID: "Login", message: lockoutMessage, submessage: lockoutSubmessage }),
            React.createElement(Form, null,
                React.createElement(Input, { "aria-label": i18n.t('Login.content.signInForm.formField.email.label'), placeholder: i18n.t('Login.content.signInForm.formField.email.placeholder'), label: i18n.t('Login.content.signInForm.formField.email.label'), errorMessage: errors.email, isInvalid: errors.email && touched.email ? true : false, value: values.email, testID: "email", onChangeText: handleChange('email'), onBlur: handleBlur('email'), inputMode: "email", disabled: isLocked }),
                React.createElement(InputPassword, { placeholder: i18n.t('Login.content.signInForm.formField.password.placeholder'), label: i18n.t('Login.content.signInForm.formField.password.label'), errorMessage: errors.password, testID: "password", setFieldValue: setFieldValue, onBlur: handleBlur('password'), onChangeText: handleChange('password'), value: values.password, isEyeIconVisible: !isLocked, isInvalid: errors.password && touched.password ? true : false, isDisabled: isLocked, marginTop: "$4", marginBottom: "$4" }),
                React.createElement(Link, { style: { marginTop: 10 }, testID: "Login.forgotPassword", onPress: handleClickForgotPass, IconElement: IconQuestionmark }, i18n.t('Login.content.signInForm.secondaryText')))),
        React.createElement(Footer, { buttonText: i18n.t('Login.title'), onButtonSubmit: handleSubmit, testIDButton: "Login.signIn", isButtonDisabled: isLocked || !isValid })));
};
export default connector(Login);
